<script setup lang="ts">
import { onMounted } from 'vue';

import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';

import { performanceMark } from '@libs/utils';

import {
  ItinerarySegmentType,
  isItineraryLocationSegment,
  isItineraryDateLocationSegment,
  isItinerarySimpleTransportSegment,
} from '../../models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../stores';
import { GlobalSuggestionTypeSwitch } from '../globalSuggestionType';

import AddDestinationButton from './AddDestinationButton.vue';
import ItineraryNoTransportSegment from './ItineraryNoTransportSegment.vue';
import ItineraryUpsellNoTransportSegment from './ItineraryUpsellNoTransportSegment.vue';
import ItinerarySimpleTransportSegment from './ItinerarySimpleTransportSegment.vue';
import ItineraryAccommodationSegment from './accommodation/ItineraryAccommodationSegment.vue';
import ItineraryActivitySegment from './acitivity/ItineraryActivitySegment.vue';
import ItineraryFerrySegment from './ferry/ItineraryFerrySegment.vue';
import ItineraryFlightSegment from './flight/ItineraryFlightSegment.vue';
import ItineraryLocationSegment from './location/ItineraryLocationSegment.vue';
import ItineraryDateLocationSegment from './location/ItineraryDateLocationSegment.vue';
import ItineraryUpsellSegment from './upsell/ItineraryUpsellSegment.vue';
import ItineraryOwnStaySegment from './ownStay/ItineraryOwnStaySegment.vue';
import ItineraryStenaUpsellNoTransportSegment from './ItineraryStenaUpsellNoTransportSegment.vue';

interface TripItineraryProps {
  printMode: boolean;
}

const props = defineProps<TripItineraryProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();

onMounted(() => {
  if (tripOverviewPageStore.isFirstTripItineraryRender) {
    performanceMark('FirstItineraryRendered');
    tripOverviewPageStore.isFirstTripItineraryRender = false;
  }
});

function openPriceCalendar(): void {
  tripOverviewPanelsStore.openConfigurationPanel(
    TabKey.Calendar,
    'low-price-calendar'
  );
}
</script>

<template>
  <div class="trip-itinerary">
    <div v-if="!props.printMode" class="global-suggestion">
      <GlobalSuggestionTypeSwitch
        @low-price-calendar-click="openPriceCalendar"
      />
    </div>
    <div
      v-for="(segment, index) in tripOverviewPageStore.tripItinerary"
      :key="index"
      :class="{
        'trip-itinerary-first-segment': index === 0,
      }"
    >
      <ItineraryDateLocationSegment
        v-if="isItineraryDateLocationSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
        @segment-clicked="
          tripOverviewPanelsStore.openConfigurationPanel(
            TabKey.Itinerary,
            'date-location-segment'
          )
        "
      />
      <ItineraryLocationSegment
        v-if="isItineraryLocationSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.NoTransport &&
          !props.printMode
        "
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.UpsellNoTransport &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItineraryStenaUpsellNoTransportSegment
        v-if="
          segment.segmentType === ItinerarySegmentType.StenaUpsellNoTransport &&
          !props.printMode
        "
        :segment="segment"
      />

      <ItinerarySimpleTransportSegment
        v-if="isItinerarySimpleTransportSegment(segment)"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryAccommodationSegment
        v-if="segment.segmentType === ItinerarySegmentType.Accommodation"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryOwnStaySegment
        v-if="segment.segmentType === ItinerarySegmentType.OwnStay"
        :segment="segment"
      />

      <ItineraryActivitySegment
        v-if="segment.segmentType === ItinerarySegmentType.Activity"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryFerrySegment
        v-if="segment.segmentType === ItinerarySegmentType.Ferry"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryFlightSegment
        v-if="segment.segmentType === ItinerarySegmentType.Flight"
        :segment="segment"
        :print-mode="props.printMode"
      />

      <ItineraryUpsellSegment
        v-else-if="
          segment.segmentType === ItinerarySegmentType.Upsell &&
          !props.printMode
        "
        :data="segment.productCardData"
        :segment-type="segment.segmentType"
      />
    </div>

    <AddDestinationButton v-if="!props.printMode" />
  </div>
</template>

<style scoped lang="scss">
.trip-itinerary {
  .global-suggestion {
    box-shadow: 0px 3px 4px -1px rgba(32, 47, 71, 0.08);
    position: relative;

    @include for-more-or-equal-than-1200-px {
      position: sticky;
      top: 0;
      z-index: var(--modal-overlay-z-index);
    }
  }
}
.trip-itinerary-first-segment {
  margin-top: var(--spacing-24);
}
</style>
